import type { ConversationEntry, TranscriptEntry } from '../store/conversationSlice';
import { isWithinMatchWindow } from './sessionHistory';

/**
 * Standalone (desktop) calls only; Recall labels each bot participant itself.
 * The rules mirror the desktop apps exactly (macOS `ParticipantLinesMoved.swift`,
 * WPF `ConversationViewModel.LinesMoved.cs`), per salesguru-workspace
 * `docs/REALTIME_ARCHITECTURE.md` §10 "Lines moved to a newcomer". Pure, so
 * `participantLinesMoved.test.ts` pins them.
 */

type Role = TranscriptEntry['speaker'];

export interface MovedLine {
  /** The line text exactly as the app received it in its transcript frame. */
  text: string;
  /** The timestamp that line's cards carry in their frames. */
  timestamp?: string;
}

/** `participant_lines_moved`: AWS put a newcomer's first lines under `from`; they are `into`'s. */
export interface ParticipantLinesMove {
  fromParticipantId: string;
  intoParticipantId: string;
  intoName?: string;
  intoRole: Role;
  lines: MovedLine[];
}

/** `participant_reclassified` in merge shape: `from` is `into` (the rep's "Same as", or an automatic split fix). */
export interface ParticipantMerge {
  fromParticipantId: string;
  intoParticipantId: string;
  intoName?: string;
  intoRole: Role;
}

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const asId = (value: unknown): string | undefined =>
  (typeof value === 'string' && value) || typeof value === 'number' ? String(value) : undefined;

/** Whitespace-normalised, as the services compare line texts. */
const normalized = (text?: string): string => (text ?? '').split(/\s+/).filter(Boolean).join(' ');

/**
 * Null for a malformed message: a missing `fromParticipantId` /
 * `intoParticipantId`, the two equal, an `intoRole` that is neither customer
 * nor salesperson, or `lines` that is not a list. An entry without text moves
 * nothing and is dropped.
 */
export const parseParticipantLinesMove = (data: unknown): ParticipantLinesMove | null => {
  const payload = (data ?? {}) as Record<string, unknown>;
  const from = asId(payload.fromParticipantId);
  const into = asId(payload.intoParticipantId);
  const role = payload.intoRole;
  if (!from || !into || from === into || (role !== 'customer' && role !== 'salesperson')) return null;
  if (!Array.isArray(payload.lines)) return null;
  const lines = payload.lines
    .map((line) => (line ?? {}) as Record<string, unknown>)
    .filter((line) => normalized(asText(line.text)))
    .map((line) => ({ text: line.text as string, timestamp: asText(line.timestamp) }));
  return { fromParticipantId: from, intoParticipantId: into, intoName: asText(payload.intoName), intoRole: role, lines };
};

/**
 * The merge shape of `participant_reclassified`, or null for any other shape.
 * An absent `intoRole` (an older server) keeps the historical salesperson
 * merge, as on the desktops (`LiveVoiceClassification.mergeRole`).
 */
export const parseParticipantMerge = (data: unknown): ParticipantMerge | null => {
  const payload = (data ?? {}) as Record<string, unknown>;
  const from = asId(payload.fromParticipantId);
  const into = asId(payload.intoParticipantId);
  if (!from || !into || from === into) return null;
  return {
    fromParticipantId: from,
    intoParticipantId: into,
    intoName: asText(payload.intoName),
    intoRole: payload.intoRole === 'customer' ? 'customer' : 'salesperson',
  };
};

/** One entry's identity across repeats of the same message. */
export const movedLineKey = (move: ParticipantLinesMove, line: MovedLine): string =>
  [move.fromParticipantId, move.intoParticipantId, normalized(line.text), line.timestamp ?? ''].join('\u001f');

export interface LineMoveOutcome {
  transcripts: TranscriptEntry[];
  /** Entries applied so far this session, so a repeated message moves nothing. */
  appliedKeys: string[];
  movedCount: number;
}

/**
 * Each entry moves ONE row: the most recent row of `from` whose normalised
 * text equals the entry's. The row takes `into`'s id, name and role. An entry
 * already applied is skipped; one with no matching row is skipped too.
 */
export const moveTranscriptLines = (
  transcripts: TranscriptEntry[],
  move: ParticipantLinesMove,
  appliedKeys: string[]
): LineMoveOutcome => {
  const outcome: LineMoveOutcome = { transcripts: [...transcripts], appliedKeys: [...appliedKeys], movedCount: 0 };
  move.lines.forEach((line) => {
    const key = movedLineKey(move, line);
    if (outcome.appliedKeys.includes(key)) return;
    const wanted = normalized(line.text);
    const index = findLastIndex(
      outcome.transcripts,
      (row) => row.participantId === move.fromParticipantId && normalized(row.text) === wanted
    );
    if (index === -1) return;
    const row = outcome.transcripts[index];
    outcome.transcripts[index] = {
      ...row,
      participantId: move.intoParticipantId,
      speaker: move.intoRole,
      speakerName: move.intoName ?? row.speakerName,
    };
    outcome.appliedKeys.push(key);
    outcome.movedCount += 1;
  });
  return outcome;
};

const findLastIndex = <T>(rows: T[], matches: (row: T) => boolean): number => {
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    if (matches(rows[index])) return index;
  }
  return -1;
};

/** A card moves with the lines when `from` raised it and it carries one of the entries' timestamps (within 1 s). */
export const raisedByMovedLine = (move: ParticipantLinesMove, participantId?: string, timestamp?: string): boolean =>
  participantId === move.fromParticipantId &&
  move.lines.some((line) => !!line.timestamp && isWithinMatchWindow(line.timestamp, timestamp));

/**
 * Objection / FAQ / new-inquiry cards raised by the moved lines go to `into`,
 * or are removed when `into` is a salesperson: a salesperson never keeps a
 * customer concern.
 */
export const moveQuestionCards = (entries: ConversationEntry[], move: ParticipantLinesMove): ConversationEntry[] =>
  entries.flatMap((entry) => {
    if (!raisedByMovedLine(move, entry.participantId, entry.timestamp)) return [entry];
    if (move.intoRole !== 'customer') return [];
    return [{ ...entry, participantId: move.intoParticipantId, speakerName: move.intoName ?? entry.speakerName }];
  });

interface RaisedAlert {
  participantId?: string;
  speaker: string;
  timestamp?: string;
  detected_at?: string;
}

/**
 * Landmine and competitor alerts raised by the moved lines are relabelled,
 * never removed: they take `into`'s id and role, which is what their
 * "You" / "Customer" label reads. The frame's `timestamp` is the raising line's.
 */
export const relabelMovedAlerts = <T extends RaisedAlert>(alerts: T[], move: ParticipantLinesMove): T[] =>
  alerts.map((alert) =>
    raisedByMovedLine(move, alert.participantId, alert.timestamp ?? alert.detected_at)
      ? { ...alert, participantId: move.intoParticipantId, speaker: move.intoRole }
      : alert
  );

/** Fold `from`'s displayed lines into `into`: same id, name and role as the target. */
export const mergeTranscriptLines = (transcripts: TranscriptEntry[], merge: ParticipantMerge): TranscriptEntry[] =>
  transcripts.map((row) =>
    row.participantId === merge.fromParticipantId
      ? {
          ...row,
          participantId: merge.intoParticipantId,
          speaker: merge.intoRole,
          speakerName: merge.intoName ?? row.speakerName,
        }
      : row
  );
